import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Scissors, KeyRound, Loader2, Check, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying | valid | invalid | saving | success
  const [meta, setMeta] = useState(null);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    axios.get(`${API}/auth/reset-password/verify?token=${token}`)
      .then(res => {
        if (res.data?.valid) {
          setMeta(res.data);
          setStatus("valid");
        } else {
          setStatus("invalid");
        }
      })
      .catch(() => setStatus("invalid"));
  }, [token]);

  const submit = async () => {
    setError("");
    if (pwd.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (pwd !== pwd2) { setError("Passwords don't match"); return; }
    setStatus("saving");
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
      setStatus("success");
      setTimeout(() => navigate("/login", { replace: true }), 2200);
    } catch (e) {
      setError(e.response?.data?.detail || "Something went wrong. Try again.");
      setStatus("valid");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-rose-50 flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="flex items-center gap-2 mb-10">
          <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-rose-500 via-violet-500 to-teal-500 flex items-center justify-center">
            <Scissors className="w-4 h-4 text-white" strokeWidth={2.25} />
          </div>
          <span className="font-['Outfit'] text-lg font-semibold tracking-tight text-slate-900">SalonFlow</span>
        </div>

        {status === "verifying" && (
          <div className="bg-white border border-slate-200 rounded-3xl p-8 text-center">
            <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-3" />
            <p className="text-sm text-slate-500 font-['Manrope']">Verifying your link…</p>
          </div>
        )}

        {status === "invalid" && (
          <div className="bg-white border border-slate-200 rounded-3xl p-8" data-testid="reset-invalid">
            <div className="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 mb-1">Link no longer valid</h1>
            <p className="text-sm text-slate-500 font-['Manrope'] mb-5">
              This password reset link has expired or already been used. Ask your salon owner to send a fresh one.
            </p>
            <Link to="/login">
              <Button className="rounded-full bg-slate-900 text-white hover:bg-slate-800 h-10 px-5">Back to sign in</Button>
            </Link>
          </div>
        )}

        {(status === "valid" || status === "saving") && (
          <div className="bg-white border border-slate-200 rounded-3xl p-8">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-rose-500 via-violet-500 to-teal-500 flex items-center justify-center mb-4">
              <KeyRound className="w-5 h-5 text-white" />
            </div>
            <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 mb-1">Set a new password</h1>
            <p className="text-sm text-slate-500 font-['Manrope'] mb-6">
              Hi{meta?.name ? `, ${meta.name.split(" ")[0]}` : ""} — choose a new password for <span className="font-medium text-slate-700">{meta?.email}</span>.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1 block font-['Manrope']">New Password</label>
                <Input data-testid="public-reset-pwd" type="password" value={pwd} onChange={e => setPwd(e.target.value)}
                  placeholder="At least 8 characters" autoFocus className="h-11 border-slate-200 rounded-xl" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1 block font-['Manrope']">Confirm</label>
                <Input data-testid="public-reset-pwd2" type="password" value={pwd2} onChange={e => setPwd2(e.target.value)}
                  className="h-11 border-slate-200 rounded-xl" />
              </div>
              {error && <p className="text-[12px] text-rose-600 font-['Manrope']" data-testid="reset-error">{error}</p>}
              <Button onClick={submit} disabled={status === "saving"}
                data-testid="public-reset-submit"
                className="w-full h-11 rounded-full bg-slate-900 text-white hover:bg-slate-800 mt-2">
                {status === "saving" ? "Saving…" : "Update password"}
              </Button>
            </div>
          </div>
        )}

        {status === "success" && (
          <div className="bg-white border border-emerald-200 rounded-3xl p-8" data-testid="reset-success">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-4">
              <Check className="w-5 h-5 text-emerald-600" />
            </div>
            <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 mb-1">Password updated</h1>
            <p className="text-sm text-slate-500 font-['Manrope']">Taking you to sign in…</p>
          </div>
        )}
      </div>
    </div>
  );
}
